import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Map, 
  Radar, 
  Target, 
  TrendingUp,
  Building2,
  DollarSign,
  Eye,
  ChevronRight,
  MapPin,
  Activity,
  Sparkles,
  Zap,
  ArrowUpRight,
  CircleDot,
  BarChart3,
  Globe,
  Crosshair,
  Lightbulb,
  Star,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { APIProvider, Map as GoogleMap, Marker } from '@vis.gl/react-google-maps';

const API = process.env.REACT_APP_BACKEND_URL || '';
const GOOGLE_MAPS_API_KEY = 'AIzaSyBps-qWONLhpTntEogvkMpzs9gRCeY3a0c';

// Momentum status colors
const MOMENTUM_COLORS = {
  premium: { bg: 'bg-[#B89B5E]', text: 'text-[#B89B5E]', border: 'border-[#B89B5E]', label: 'Premium Opportunity' },
  healthy: { bg: 'bg-emerald-500', text: 'text-emerald-500', border: 'border-emerald-500', label: 'Healthy Momentum' },
  stalled: { bg: 'bg-red-500', text: 'text-red-500', border: 'border-red-500', label: 'Needs Attention' },
  inactive: { bg: 'bg-slate-400', text: 'text-slate-400', border: 'border-slate-400', label: 'Inactive' }
};

// Calculate momentum status based on activity
const getMomentumStatus = (territory) => {
  // Premium: High value territories ($100k+ pipeline or 3+ opportunities)
  if (territory.pipeline_value >= 100000 || territory.opportunities >= 3) return 'premium';
  // Healthy: Active territories with multiple opportunities or decent pipeline
  if (territory.opportunities >= 2 || territory.pipeline_value >= 50000) return 'healthy';
  // Stalled: Has opportunities but low engagement
  if (territory.opportunities > 0) return 'stalled';
  return 'inactive';
};

// Generate strategic insights based on data
const generateInsights = (territories) => {
  const insights = [];
  
  // Find territory with highest pipeline
  const topPipeline = territories.reduce((max, t) => t.pipeline_value > max.pipeline_value ? t : max, territories[0]);
  if (topPipeline && topPipeline.pipeline_value > 0) {
    insights.push({
      icon: Star,
      text: `${topPipeline.name} has the highest concentration of premium hospitality opportunities.`,
      type: 'highlight'
    });
  }
  
  // Find territory with most signals
  const mostSignals = territories.reduce((max, t) => t.signals > max.signals ? t : max, territories[0]);
  if (mostSignals && mostSignals.signals > 2) {
    insights.push({
      icon: Radar,
      text: `${mostSignals.name} is showing strong market signal activity with ${mostSignals.signals} signals detected.`,
      type: 'signal'
    });
  }
  
  // Find territory with Vision Deck engagement
  const visionDeckTerritory = territories.find(t => t.vision_decks_sent > 0);
  if (visionDeckTerritory) {
    insights.push({
      icon: Sparkles,
      text: `${visionDeckTerritory.name} has active Vision Deck engagement.`,
      type: 'engagement'
    });
  }
  
  // Find territories with signals but no opportunities
  const untappedTerritory = territories.find(t => t.signals > 0 && t.opportunities === 0);
  if (untappedTerritory) {
    insights.push({
      icon: AlertTriangle,
      text: `${untappedTerritory.name} has signals but no active pursuit - potential untapped market.`,
      type: 'warning'
    });
  }
  
  // Add momentum insight
  const healthyCount = territories.filter(t => getMomentumStatus(t) === 'healthy' || getMomentumStatus(t) === 'premium').length;
  if (healthyCount > 0) {
    insights.push({
      icon: TrendingUp,
      text: `${healthyCount} territories showing positive momentum this quarter.`,
      type: 'momentum'
    });
  }
  
  return insights.slice(0, 4);
};

// Generate opportunity predictions based on signals
const generatePredictions = (signals, opportunities) => {
  const predictions = [];
  
  // Group signals by company/property
  const signalsByProperty = {};
  signals?.forEach(signal => {
    const key = signal.property_name || signal.company_name || signal.title?.split(' - ')[0] || 'Unknown Property';
    if (!signalsByProperty[key]) {
      signalsByProperty[key] = {
        name: key,
        city: signal.location?.city || signal.city || 'Unknown',
        signals: [],
        signalTypes: new Set()
      };
    }
    signalsByProperty[key].signals.push(signal);
    signalsByProperty[key].signalTypes.add(signal.signal_type || signal.type || 'market');
  });
  
  // Score and rank predictions
  Object.values(signalsByProperty).forEach(property => {
    const signalCount = property.signals.length;
    const signalDiversity = property.signalTypes.size;
    
    // Calculate confidence score
    let confidence = 'low';
    let confidenceScore = signalCount * 20 + signalDiversity * 15;
    
    if (confidenceScore >= 70) confidence = 'high';
    else if (confidenceScore >= 40) confidence = 'medium';
    
    // Estimate deal value based on signals
    const estimatedValue = Math.round((30000 + signalCount * 25000 + signalDiversity * 15000) / 10000) * 10000;
    
    // Check if already an opportunity
    const existingOpp = opportunities?.find(o => 
      o.company_name?.toLowerCase().includes(property.name.toLowerCase()) ||
      property.name.toLowerCase().includes(o.company_name?.toLowerCase() || '')
    );
    
    if (!existingOpp && signalCount >= 1) {
      predictions.push({
        name: property.name,
        city: property.city,
        confidence,
        confidenceScore,
        signalCount,
        signalTypes: Array.from(property.signalTypes),
        estimatedValue,
        signals: property.signals.slice(0, 3)
      });
    }
  });
  
  return predictions.sort((a, b) => b.confidenceScore - a.confidenceScore).slice(0, 6);
};

export default function Territories() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('command');
  const [territories, setTerritories] = useState([]);
  const [mapData, setMapData] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [predictions, setPredictions] = useState([]);

  useEffect(() => {
    fetchTerritoryData();
    // fetchTerritoryData is intentionally not in the dep array — this effect
    // only runs once on mount. The function is defined in the same component
    // and closes over stable refs, so adding it would force a useCallback
    // wrap without changing behaviour.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTerritoryData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch opportunities directly for better location data
      const oppResponse = await fetch(`${API}/api/opportunities`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Fetch map data for signals
      const mapResponse = await fetch(`${API}/api/market-intelligence/map-data`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      let opportunitiesData = [];
      let signals = [];
      
      if (oppResponse.ok) {
        opportunitiesData = await oppResponse.json();
        setOpportunities(opportunitiesData);
      }
      
      if (mapResponse.ok) {
        const mapDataRes = await mapResponse.json();
        signals = mapDataRes.signals || [];
        setMapData({ ...mapDataRes, opportunities: opportunitiesData });
      }
      
      // Aggregate by territory/city from opportunities
      const territoryMap = {};
      
      // List of invalid territory names (too broad or not actual cities)
      const invalidTerritoryNames = ['Europe', 'North America', 'Asia', 'Africa', 'South America', 'Australia', 'None', 'N/A', 'Unknown', ''];
      
      opportunitiesData.forEach(opp => {
        // Try multiple sources for city name - prioritize actual city data
        let city = opp.location?.city || 
                   opp.city ||
                   null;
        
        // Only use region if it looks like a city (contains comma suggesting "City, Province" format)
        if (!city && opp.region && opp.region.includes(',')) {
          city = opp.region.split(',')[0]?.trim();
        }
        
        // Skip if no valid city or if it's a broad region name
        if (!city || invalidTerritoryNames.includes(city)) return;
        
        if (!territoryMap[city]) {
          territoryMap[city] = {
            name: city,
            signals: 0,
            opportunities: 0,
            pipeline_value: 0,
            vision_decks_sent: 0,
            engagement_rate: 0,
            properties: [],
            province: opp.location?.province_state || opp.region?.split(',')[1]?.trim() || ''
          };
        }
        territoryMap[city].opportunities += 1;
        territoryMap[city].pipeline_value += opp.deal_value || 0;
        if (opp.company_name) {
          territoryMap[city].properties.push(opp.company_name);
        }
      });
      
      // Add signal counts
      signals.forEach(signal => {
        const city = signal.location?.city || signal.city;
        if (city && territoryMap[city]) {
          territoryMap[city].signals += 1;
        }
      });
      
      const sortedTerritories = Object.values(territoryMap).sort((a, b) => b.pipeline_value - a.pipeline_value);
      setTerritories(sortedTerritories);
      
      // Generate predictions
      const preds = generatePredictions(signals, opportunitiesData);
      setPredictions(preds);
    } catch (error) {
      console.error('Error fetching territory data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toLocaleString()}`;
  };

  const totalPipelineValue = territories.reduce((sum, t) => sum + t.pipeline_value, 0);
  const totalOpportunities = territories.reduce((sum, t) => sum + t.opportunities, 0);
  const totalSignals = mapData?.signals?.length || 0;
  
  const insights = useMemo(() => generateInsights(territories), [territories]);

  const getConfidenceBadge = (confidence) => {
    switch (confidence) {
      case 'high':
        return <Badge className="bg-emerald-500 text-white">High Confidence</Badge>;
      case 'medium':
        return <Badge className="bg-amber-500 text-white">Medium Confidence</Badge>;
      default:
        return <Badge variant="outline" className="text-muted-foreground">Emerging</Badge>;
    }
  };

  // Create opportunity from a territory intelligence prediction
  const createOpportunityFromPrediction = async (prediction) => {
    const token = localStorage.getItem('token');
    
    try {
      toast.info(`Creating opportunity for ${prediction.name}...`);
      
      // Find the signal ID if available
      const signalId = prediction.signals?.[0]?.id;
      
      // Create opportunity payload
      const opportunityData = {
        company_name: prediction.name,
        property_type: 'Business Services',
        stage: 'prospecting',
        probability: prediction.confidence === 'high' ? 40 : prediction.confidence === 'medium' ? 25 : 15,
        deal_value: prediction.estimatedValue || 50000,
        close_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 90 days from now
        property_location: prediction.city || 'Unknown',
        signal_id: signalId,
        source: 'territory_intelligence',
        notes: `Auto-created from Territory Intelligence.\nSignals: ${prediction.signalCount}\nSignal Types: ${prediction.signalTypes?.join(', ') || 'Various'}`
      };
      
      // Add location data if available from signals
      if (prediction.signals?.[0]?.location) {
        opportunityData.location = prediction.signals[0].location;
      }
      if (prediction.signals?.[0]?.geo) {
        opportunityData.geo = prediction.signals[0].geo;
      }
      
      const response = await fetch(`${API}/api/opportunities`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(opportunityData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create opportunity');
      }
      
      const newOpportunity = await response.json();
      
      toast.success(`Opportunity created for ${prediction.name}!`);
      
      // Navigate to the new opportunity
      window.location.href = `/opportunity/${newOpportunity.id}`;
      
    } catch (error) {
      console.error('Error creating opportunity:', error);
      toast.error(`Failed to create opportunity: ${error.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFFEF5] to-[#F8F7F2]">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-8 sm:mb-12">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-[#B89B5E]/10">
              <Globe className="w-6 h-6 sm:w-7 sm:h-7 text-[#B89B5E]" />
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight text-[#1A1A1A]">
              Territories
            </h1>
          </div>
          <p className="text-sm sm:text-base text-muted-foreground ml-0 sm:ml-14">
            Strategic market intelligence and geographic opportunity mapping
          </p>
        </div>

        {/* Premium KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-8 sm:mb-12">
          {/* Territories in Play */}
          <Card className="bg-white/80 backdrop-blur border-[#E8E6E1] hover:shadow-lg transition-all duration-300">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 rounded-lg bg-[#B89B5E]/10">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-[#B89B5E]" />
                </div>
                <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Active</span>
              </div>
              <p className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#1A1A1A] mb-1">{territories.length}</p>
              <p className="text-xs sm:text-sm text-muted-foreground">Territories in Play</p>
            </CardContent>
          </Card>
          
          {/* Market Signals Detected */}
          <Card className="bg-white/80 backdrop-blur border-[#E8E6E1] hover:shadow-lg transition-all duration-300">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <Radar className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                </div>
                <span className="text-xs text-emerald-600 font-medium uppercase tracking-wider">Signals</span>
              </div>
              <p className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#1A1A1A] mb-1">{totalSignals}</p>
              <p className="text-xs sm:text-sm text-muted-foreground">Market Signals Detected</p>
            </CardContent>
          </Card>
          
          {/* Active Transformations */}
          <Card className="bg-white/80 backdrop-blur border-[#E8E6E1] hover:shadow-lg transition-all duration-300">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <Target className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                </div>
                <span className="text-xs text-blue-600 font-medium uppercase tracking-wider">Active</span>
              </div>
              <p className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#1A1A1A] mb-1">{totalOpportunities}</p>
              <p className="text-xs sm:text-sm text-muted-foreground">Active Transformations</p>
            </CardContent>
          </Card>
          
          {/* Opportunity Value in Motion */}
          <Card className="bg-white/80 backdrop-blur border-[#E8E6E1] hover:shadow-lg transition-all duration-300">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="p-2 rounded-lg bg-[#B89B5E]/10">
                  <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-[#B89B5E]" />
                </div>
                <span className="text-xs text-[#B89B5E] font-medium uppercase tracking-wider">Value</span>
              </div>
              <p className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#1A1A1A] mb-1">{formatCurrency(totalPipelineValue)}</p>
              <p className="text-xs sm:text-sm text-muted-foreground">Opportunity Value in Motion</p>
            </CardContent>
          </Card>
        </div>

        {/* Territory Command Center */}
        <Card className="bg-white/90 backdrop-blur border-[#E8E6E1] shadow-lg mb-8 sm:mb-12 overflow-hidden">
          <CardHeader className="border-b border-[#E8E6E1] bg-gradient-to-r from-[#1A1A1A] to-[#2D2D2D] text-white p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="text-lg sm:text-xl lg:text-2xl font-light flex items-center gap-3">
                  <Crosshair className="w-5 h-5 sm:w-6 sm:h-6 text-[#B89B5E]" />
                  Territory Command Center
                </CardTitle>
                <p className="text-xs sm:text-sm text-gray-400 mt-1">
                  See where momentum is building and where your next transformation begins.
                </p>
              </div>
              <Button 
                asChild 
                className="bg-[#B89B5E] hover:bg-[#A08050] text-white w-full sm:w-auto"
              >
                <Link to="/market-intelligence">
                  <Globe className="w-4 h-4 mr-2" />
                  Explore Strategic Markets
                </Link>
              </Button>
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            <div className="grid grid-cols-1 lg:grid-cols-3">
              {/* Map Area */}
              <div className="lg:col-span-2 p-4 sm:p-6 border-b lg:border-b-0 lg:border-r border-[#E8E6E1]">
                <div className="aspect-[16/10] sm:aspect-[16/9] rounded-xl overflow-hidden relative">
                  <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                    <GoogleMap
                      mapId="territories-command-map"
                      defaultCenter={{ lat: 54.0, lng: -105.0 }}
                      defaultZoom={4}
                      gestureHandling="cooperative"
                      disableDefaultUI={false}
                      mapTypeControl={false}
                      streetViewControl={false}
                      fullscreenControl={false}
                      style={{ width: '100%', height: '100%' }}
                    >
                      {/* Territory Markers */}
                      {opportunities.filter(opp => opp.geo?.lat && opp.geo?.lng).map((opp, index) => {
                        const territoryName = opp.location?.city || opp.region?.split(',')[0] || 'Unknown';
                        const territory = territories.find(t => t.name === territoryName);
                        const momentum = territory ? getMomentumStatus(territory) : 'stalled';
                        
                        // Determine marker color based on momentum status
                        const colorHex = momentum === 'premium' ? '%23B89B5E' : 
                                        momentum === 'healthy' ? '%2310B981' : 
                                        momentum === 'stalled' ? '%23EF4444' : '%2394A3B8';
                        
                        // Create SVG data URL for colored pin marker
                        const svgPin = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40"><path fill="${decodeURIComponent(colorHex)}" d="M16 0C7.2 0 0 7.2 0 16c0 12 16 24 16 24s16-12 16-24c0-8.8-7.2-16-16-16zm0 22c-3.3 0-6-2.7-6-6s2.7-6 6-6 6 2.7 6 6-2.7 6-6 6z"/><circle cx="16" cy="16" r="4" fill="white"/></svg>`)}`;
                        
                        return (
                          <Marker
                            key={opp.id || index}
                            position={{ lat: opp.geo.lat, lng: opp.geo.lng }}
                            title={`${opp.company_name} - ${formatCurrency(opp.deal_value || 0)} (${MOMENTUM_COLORS[momentum]?.label || 'Unknown'})`}
                            icon={{
                              url: svgPin,
                              scaledSize: { width: 32, height: 40 },
                              anchor: { x: 16, y: 40 }
                            }}
                          />
                        );
                      })}
                    </GoogleMap>
                  </APIProvider>
                  
                  {/* Legend Overlay */}
                  <div className="absolute bottom-3 left-3 flex flex-wrap gap-2 z-10">
                    {Object.entries(MOMENTUM_COLORS).slice(0, 3).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-1 bg-white/90 backdrop-blur px-2 py-1 rounded text-xs shadow">
                        <div className={`w-2 h-2 rounded-full ${value.bg}`} />
                        <span className="text-gray-700">{value.label}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* View Full Map Button */}
                  <Button 
                    asChild 
                    variant="outline" 
                    className="absolute bottom-3 right-3 bg-white/90 hover:bg-white shadow z-10"
                  >
                    <Link to="/market-intelligence">
                      <Map className="w-4 h-4 mr-2" />
                      Open Full Map
                    </Link>
                  </Button>
                </div>
              </div>
              
              {/* Territory Intelligence Panel */}
              <div className="p-4 sm:p-6">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Territory Intelligence
                </h3>
                
                <ScrollArea className="h-[300px] sm:h-[350px] pr-2">
                  <div className="space-y-3">
                    {loading ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="w-6 h-6 border-2 border-[#B89B5E] border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : territories.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <MapPin className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No territory data available</p>
                      </div>
                    ) : (
                      territories.map((territory, index) => {
                        const momentum = getMomentumStatus(territory);
                        return (
                          <Link 
                            key={territory.name}
                            to={`/pipeline?city=${encodeURIComponent(territory.name)}`}
                            className={`block p-3 rounded-lg border ${index === 0 ? 'border-[#B89B5E] bg-[#B89B5E]/5' : 'border-[#E8E6E1] bg-white/50'} hover:shadow-md hover:border-[#B89B5E]/50 transition-all cursor-pointer group`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${MOMENTUM_COLORS[momentum].bg}`} />
                                <span className="font-medium text-[#1A1A1A] text-sm">{territory.name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {index === 0 && (
                                  <Badge className="bg-[#B89B5E] text-white text-xs">Top</Badge>
                                )}
                                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#B89B5E] transition-colors" />
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-muted-foreground">Pipeline</span>
                                <p className="font-medium text-[#1A1A1A]">{formatCurrency(territory.pipeline_value)}</p>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Opportunities</span>
                                <p className="font-medium text-[#1A1A1A]">{territory.opportunities}</p>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Signals</span>
                                <p className="font-medium text-[#1A1A1A]">{territory.signals}</p>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Vision Decks</span>
                                <p className="font-medium text-[#1A1A1A]">{territory.vision_decks_sent}</p>
                              </div>
                            </div>
                          </Link>
                        );
                      })
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Strategic Insights */}
        {insights.length > 0 && (
          <Card className="bg-gradient-to-r from-[#1A1A1A] to-[#2D2D2D] border-0 shadow-lg mb-8 sm:mb-12">
            <CardHeader className="pb-2 sm:pb-4">
              <CardTitle className="text-base sm:text-lg font-light text-white flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-[#B89B5E]" />
                Strategic Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {insights.map((insight, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-3 p-3 sm:p-4 rounded-lg bg-white/5 backdrop-blur border border-white/10"
                  >
                    <div className="p-2 rounded-lg bg-[#B89B5E]/20 shrink-0">
                      <insight.icon className="w-4 h-4 text-[#B89B5E]" />
                    </div>
                    <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">{insight.text}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Opportunity Predictions */}
        <Card className="bg-white/90 backdrop-blur border-[#E8E6E1] shadow-lg mb-8 sm:mb-12">
          <CardHeader className="border-b border-[#E8E6E1]">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div>
                <CardTitle className="text-lg sm:text-xl font-light text-[#1A1A1A] flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  Opportunity Predictions
                </CardTitle>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Properties showing signals that suggest upcoming uniform program opportunities
                </p>
              </div>
              <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 w-fit">
                <Activity className="w-3 h-3 mr-1" />
                AI-Powered
              </Badge>
            </div>
          </CardHeader>
          
          <CardContent className="p-4 sm:p-6">
            {predictions.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Radar className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="text-sm">No predictions available yet</p>
                <p className="text-xs mt-1">Predictions are generated from market signals</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {predictions.map((prediction, index) => (
                  <Card 
                    key={index}
                    className={`border hover:shadow-lg transition-all duration-300 ${
                      prediction.confidence === 'high' 
                        ? 'border-emerald-200 bg-emerald-50/30' 
                        : prediction.confidence === 'medium'
                        ? 'border-amber-200 bg-amber-50/30'
                        : 'border-[#E8E6E1]'
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-[#1A1A1A] text-sm truncate">{prediction.name}</h4>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3 h-3" />
                            {prediction.city}
                          </p>
                        </div>
                        {getConfidenceBadge(prediction.confidence)}
                      </div>
                      
                      <div className="space-y-2 mb-3">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Signals Detected</span>
                          <span className="font-medium text-[#1A1A1A] flex items-center gap-1">
                            <Radar className="w-3 h-3 text-[#B89B5E]" />
                            {prediction.signalCount}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">Potential Value</span>
                          <span className="font-medium text-[#B89B5E]">{formatCurrency(prediction.estimatedValue)}</span>
                        </div>
                      </div>
                      
                      {/* Signal Types */}
                      <div className="flex flex-wrap gap-1 mb-3">
                        {prediction.signalTypes.slice(0, 3).map((type, i) => (
                          <Badge key={i} variant="outline" className="text-xs capitalize">
                            {type.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </div>
                      
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full text-xs hover:bg-[#B89B5E] hover:text-white hover:border-[#B89B5E] transition-colors"
                        onClick={() => createOpportunityFromPrediction(prediction)}
                      >
                        Create Opportunity
                        <ArrowUpRight className="w-3 h-3 ml-1" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Button 
            asChild 
            variant="outline" 
            className="h-auto py-4 px-6 justify-start border-[#E8E6E1] hover:border-[#B89B5E] hover:bg-[#B89B5E]/5"
          >
            <Link to="/market-intelligence" className="flex items-center gap-4">
              <div className="p-2 rounded-lg bg-[#B89B5E]/10">
                <Radar className="w-5 h-5 text-[#B89B5E]" />
              </div>
              <div className="text-left">
                <span className="font-medium text-[#1A1A1A] block">Market Radar</span>
                <span className="text-xs text-muted-foreground">Monitor real-time signals</span>
              </div>
              <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
            </Link>
          </Button>
          
          <Button 
            asChild 
            variant="outline" 
            className="h-auto py-4 px-6 justify-start border-[#E8E6E1] hover:border-[#B89B5E] hover:bg-[#B89B5E]/5"
          >
            <Link to="/pipeline" className="flex items-center gap-4">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div className="text-left">
                <span className="font-medium text-[#1A1A1A] block">Style Opportunities</span>
                <span className="text-xs text-muted-foreground">View active pipeline</span>
              </div>
              <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
            </Link>
          </Button>
          
          <Button 
            asChild 
            variant="outline" 
            className="h-auto py-4 px-6 justify-start border-[#E8E6E1] hover:border-[#B89B5E] hover:bg-[#B89B5E]/5"
          >
            <Link to="/vision-decks" className="flex items-center gap-4">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <div className="text-left">
                <span className="font-medium text-[#1A1A1A] block">Vision Decks</span>
                <span className="text-xs text-muted-foreground">Create proposals</span>
              </div>
              <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
            </Link>
          </Button>
        </div>
      </main>
    </div>
  );
}
